import { Transform, Type } from 'class-transformer';
import {
	ArrayMaxSize,
	IsArray,
	IsBoolean,
	IsEnum,
	IsIn,
	IsInt,
	IsOptional,
	IsString,
	IsUUID,
	Matches,
	Max,
	MaxLength,
	Min,
} from 'class-validator';
import { CoursesJournalStatus } from '../../../enums/courses.enum';
import { UserRole } from '../../../enums/user-role.enum';
import { toStringList } from './courses-dto.util';

export const COURSES_ASSIGNABLE_ROLES = [UserRole.Mentor, UserRole.User] as const;
export type CoursesAssignableRole = (typeof COURSES_ASSIGNABLE_ROLES)[number];

const COURSES_TAG_KEY_RE = /^[a-z][a-z0-9_]{1,48}$/;

export class CoursesAdminSetUserRoleDto {
	@IsIn(COURSES_ASSIGNABLE_ROLES)
	role: CoursesAssignableRole;
}

export class CoursesAdminUserTagDto {
	@IsString()
	@Matches(COURSES_TAG_KEY_RE, { message: 'Invalid tag' })
	tag: string;
}

export const COURSES_USERS_SORT_FIELDS = ['created_at', 'full_name', 'email', 'role'] as const;
export type CoursesUsersSortField = (typeof COURSES_USERS_SORT_FIELDS)[number];

export class CoursesAdminUsersFilterDto {
	@IsOptional()
	@IsString()
	@MaxLength(200)
	q?: string;

	@IsOptional()
	@Transform(toStringList)
	@IsArray()
	@ArrayMaxSize(50)
	@Matches(COURSES_TAG_KEY_RE, { each: true, message: 'Invalid tag' })
	tags?: string[];

	@IsOptional()
	@IsIn(COURSES_ASSIGNABLE_ROLES)
	role?: CoursesAssignableRole;

	@IsOptional()
	@IsUUID()
	courseId?: string;

	@IsOptional()
	@IsIn(COURSES_USERS_SORT_FIELDS)
	sortBy?: CoursesUsersSortField;

	@IsOptional()
	@IsIn(['asc', 'desc'])
	sortDir?: 'asc' | 'desc';
}

export class CoursesListUsersQueryDto extends CoursesAdminUsersFilterDto {
	@IsOptional()
	@Type(() => Number)
	@IsInt()
	@Min(1)
	@Max(100000)
	page?: number;

	@IsOptional()
	@Type(() => Number)
	@IsInt()
	@Min(1)
	@Max(100)
	pageSize?: number;
}

export const COURSES_STUDENTS_SORT_FIELDS = ['student', 'completion', 'last_login'] as const;
export type CoursesStudentsSortField = (typeof COURSES_STUDENTS_SORT_FIELDS)[number];

export class CoursesAdminUserNeighborsQueryDto {
	@IsOptional()
	@IsString()
	@MaxLength(200)
	q?: string;

	@IsOptional()
	@Transform(toStringList)
	@IsArray()
	@ArrayMaxSize(50)
	@Matches(COURSES_TAG_KEY_RE, { each: true, message: 'Invalid tag' })
	tags?: string[];

	@IsOptional()
	@IsIn(COURSES_ASSIGNABLE_ROLES)
	role?: CoursesAssignableRole;

	@IsOptional()
	@IsUUID()
	courseId?: string;

	@IsOptional()
	@IsIn(COURSES_STUDENTS_SORT_FIELDS)
	sortBy?: CoursesStudentsSortField;

	@IsOptional()
	@IsIn(['asc', 'desc'])
	sortDir?: 'asc' | 'desc';
}

export class CoursesStudentsOverviewQueryDto {
	@IsOptional()
	@IsString()
	@MaxLength(200)
	q?: string;

	@IsOptional()
	@Transform(toStringList)
	@IsArray()
	@ArrayMaxSize(50)
	@Matches(COURSES_TAG_KEY_RE, { each: true, message: 'Invalid tag' })
	tags?: string[];

	@IsOptional()
	@IsIn(COURSES_ASSIGNABLE_ROLES)
	role?: CoursesAssignableRole;

	@IsOptional()
	@IsUUID()
	courseId?: string;

	@IsOptional()
	@IsIn(COURSES_STUDENTS_SORT_FIELDS)
	sortBy?: CoursesStudentsSortField;

	@IsOptional()
	@IsIn(['asc', 'desc'])
	sortDir?: 'asc' | 'desc';

	@IsOptional()
	@Type(() => Number)
	@IsInt()
	@Min(1)
	@Max(100000)
	page?: number;

	@IsOptional()
	@Type(() => Number)
	@IsInt()
	@Min(1)
	@Max(100)
	pageSize?: number;
}

export const COURSES_PENDING_SORT_FIELDS = [
	'date',
	'last_graded',
	'quiz',
	'student',
	'status',
	'percentage',
] as const;
export type CoursesPendingSortField = (typeof COURSES_PENDING_SORT_FIELDS)[number];

export const COURSES_PENDING_STATUS_FILTERS = [
	'ungraded',
	'graded',
	'passed',
	'failed',
	'all',
] as const;
export type CoursesPendingStatusFilter = (typeof COURSES_PENDING_STATUS_FILTERS)[number];

/** Query for `listPendingQuizAttempts`; defaults are applied in the service. */
export class CoursesListPendingQuizAttemptsQueryDto {
	@IsOptional()
	@IsString()
	@MaxLength(200)
	q?: string;

	@IsOptional()
	@IsIn(COURSES_PENDING_STATUS_FILTERS)
	status?: CoursesPendingStatusFilter;

	@IsOptional()
	@IsString()
	@MaxLength(36)
	lesson?: string;

	@IsOptional()
	@IsString()
	@MaxLength(40)
	from?: string;

	@IsOptional()
	@IsString()
	@MaxLength(40)
	to?: string;

	@IsOptional()
	@Type(() => Number)
	@IsInt()
	@Min(1)
	@Max(100000)
	page?: number;

	@IsOptional()
	@Type(() => Number)
	@IsInt()
	@Min(1)
	@Max(100)
	pageSize?: number;

	@IsOptional()
	@IsIn(COURSES_PENDING_SORT_FIELDS)
	sortBy?: CoursesPendingSortField;

	@IsOptional()
	@IsIn(['asc', 'desc'])
	sortDir?: 'asc' | 'desc';
}

export class CoursesGradeQuizAnswerDto {
	@IsBoolean()
	isCorrect: boolean;
}

export class CoursesListJournalEntriesQueryDto {
	@IsOptional()
	@IsEnum(CoursesJournalStatus)
	status?: CoursesJournalStatus;

	@IsOptional()
	@IsUUID()
	studentId?: string;

	@IsOptional()
	@Type(() => Number)
	@IsInt()
	@Min(1)
	@Max(100000)
	page?: number;
}

export class CoursesReviewJournalEntryDto {
	@IsOptional()
	@IsString()
	@MaxLength(5000)
	feedback?: string;
}
